import { useCallback, useEffect, useRef, useState } from "react";
import type { OtpChallenge } from "./otp-browser.js";

/** One request at a time; old replies and delivery timers cannot replace a newer or verified challenge. */
export function useOtpFlow(options: {
  fallback: (challenge: OtpChallenge) => Promise<OtpChallenge>;
  errorMessage?: (error: unknown) => string;
}) {
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deadline, setDeadline] = useState<number | null>(null);
  const mounted = useRef(true);
  const version = useRef(0);
  const running = useRef(false);
  const activeRequest = useRef<AbortController | null>(null);
  const current = useRef(options);
  current.current = options;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; version.current++; activeRequest.current?.abort(); }; }, []);

  const accept = useCallback((next: OtpChallenge) => {
    setChallenge(next); setCode(""); setError(null);
    setDeadline(typeof next.fallbackAfterSeconds === "number" ? Date.now() + next.fallbackAfterSeconds * 1000 : null);
  }, []);
  const clear = useCallback(() => {
    version.current++;
    activeRequest.current?.abort();
    setChallenge(null); setCode(""); setError(null); setDeadline(null);
  }, []);
  const perform = useCallback(async <T,>(operation: (signal: AbortSignal) => Promise<T>, success: (result: T) => void): Promise<void> => {
    if (running.current) return;
    running.current = true;
    const generation = version.current;
    const controller = new AbortController();
    activeRequest.current = controller;
    setBusy(true); setError(null);
    try {
      const result = await operation(controller.signal);
      if (mounted.current && generation === version.current) success(result);
    } catch (failure) {
      if (mounted.current && generation === version.current) {
        setError(current.current.errorMessage?.(failure) ?? (failure instanceof Error ? failure.message : "Verification failed. Please try again."));
      }
    } finally {
      running.current = false;
      activeRequest.current = null;
      if (mounted.current) setBusy(false);
    }
  }, []);
  const request = useCallback((operation: (signal: AbortSignal) => Promise<OtpChallenge>) => perform(operation, accept), [perform, accept]);
  const fallback = useCallback(async () => {
    if (!challenge || running.current) return;
    // Failed delivery retries need an explicit user action; never loop and send unlimited messages.
    setDeadline(null);
    await request(() => current.current.fallback(challenge));
  }, [challenge, request]);
  useEffect(() => {
    if (!challenge || deadline === null || busy || code.length > 0) return;
    const timer = setTimeout(() => void fallback(), Math.max(0, deadline - Date.now()));
    return () => clearTimeout(timer);
  }, [challenge, deadline, busy, code, fallback]);

  return { challenge, code, setCode, busy, error, setError, accept, clear, perform, request, fallback };
}
